import memoize from 'memoize-one';
import PropTypes, { objectOf, string } from 'prop-types';
import React, { createContext, useContext, useMemo, PureComponent } from 'react';
import { mergeTheme, legacyTheme, listenForRegisteredModules } from './themes';
export * from './themes';
import { createTheming, createUseStyles as createUseStyles$1 } from 'react-jss';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var theming = createTheming(createContext({}));
var withTheme = theming.withTheme,
    useTheme = theming.useTheme;
var ThemeConsumer = function ThemeConsumer(_ref) {
  var children = _ref.children;
  return children(useContext(theming.context));
};
/**
 * Utility function that merges two sets of classes into one
 * @param {Object<string>} defaultClasses - The default classes for the component
 * @param {Object<string>} overrideClasses - The classes being passed in from above
 * @returns {Object<string>} Merged classes
 */

var mergeClasses = function mergeClasses(defaultClasses, overrideClasses) {
  return Object.entries(defaultClasses).reduce(function (classes, _ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        key = _ref3[0],
        value = _ref3[1];

    return _objectSpread({}, classes, _defineProperty({}, key, overrideClasses[key] ? "".concat(value, " ").concat(overrideClasses[key]) : value));
  }, {});
};
/**
 * Thin wrapper around react-jss that adds the glu-theming context and support for merging classes
 * @param {Object|function} styles - Styles from which to create classes
 * @param {Object} [options] - Advanced usage only
 * @returns {function(*=)} useStyles hook
 */

var createUseStyles = function createUseStyles(styles) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    theming: theming
  };
  var useJSSStyles = createUseStyles$1(styles, options);

  var useStyles = function useStyles(props) {
    var _ref4 = props || {},
        propsClasses = _ref4.classes;

    var jssClasses = useJSSStyles(props);
    var classes = useMemo(function () {
      return mergeClasses(jssClasses, propsClasses || {});
    }, [jssClasses, propsClasses]);
    return classes;
  };

  return useStyles;
};
/**
 * Backwards compatibility HOC - adds classes as a prop to your component
 * @param {Object|function} styles - Styles from which to create classes
 * @param {Object} [options] - Advanced usage only
 * @returns {function(*): function} - Wrapped Component
 */

var withStyles = function withStyles(styles, options) {
  return function (Component) {
    var useStyles = createUseStyles(styles, options);

    var StyledComponent = function StyledComponent(props) {
      var classes = useStyles(props);
      var theme = useTheme();
      return React.createElement(Component, _extends({}, props, typeof styles === 'function' ? {
        theme: theme
      } : {}, {
        classes: classes
      }));
    };

    StyledComponent.propTypes = _objectSpread({}, Component.propTypes, {
      classes: objectOf(string)
    }); // StyledComponent provides the theme to the child component, it does not receive it as a prop

    delete StyledComponent.propTypes.theme;
    StyledComponent.defaultProps = _objectSpread({}, Component.defaultProps, {
      classes: undefined
    }); // istanbul ignore next

    StyledComponent.displayName = "WithStyles(".concat(Component.displayName || Component.name || 'Component', ")");
    return StyledComponent;
  };
};

function _createSuper2(Derived) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;

    try {
      Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
      return true;
    } catch (e) {
      return false;
    }
  }

  return function () {
    var Super = _getPrototypeOf(Derived),
        result;

    if (isNativeReflectConstruct()) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}
var getMergedTheme = memoize(mergeTheme);

var ThemeProvider =
/*#__PURE__*/
function (_PureComponent) {
  _inherits(ThemeProvider, _PureComponent);

  var _super = _createSuper2(ThemeProvider);

  function ThemeProvider() {
    var _this;

    _classCallCheck(this, ThemeProvider);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "state", {
      moduleCounter: 0
    });

    _defineProperty(_assertThisInitialized(_this), "updateTheme", function () {
      _this.setState(function (_ref) {
        var moduleCounter = _ref.moduleCounter;
        return {
          moduleCounter: moduleCounter + 1
        };
      });
    });

    return _this;
  }

  _createClass(ThemeProvider, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.stopListening = listenForRegisteredModules(this.updateTheme);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.stopListening();
    }
  }, {
    key: "render",
    value: function render() {
      return React.createElement(theming.ThemeProvider, {
        theme: this.state.theme
      }, this.props.children);
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(props, state) {
      return {
        theme: getMergedTheme(props.baseTheme, props.theme, state.moduleCounter)
      };
    } // eslint-disable-next-line react/state-in-constructor

  }]);

  return ThemeProvider;
}(PureComponent);
ThemeProvider.propTypes = {
  theme: PropTypes.shape({}),
  // eslint-disable-line react/no-unused-prop-types
  baseTheme: PropTypes.func,
  // eslint-disable-line react/no-unused-prop-types
  children: PropTypes.node.isRequired
};
ThemeProvider.defaultProps = {
  theme: {},
  baseTheme: legacyTheme
};

var DarkContext = createContext(false);
var DarkMode = function DarkMode(_ref) {
  var off = _ref.off,
      children = _ref.children;
  return React.createElement(DarkContext.Provider, {
    value: !off
  }, children);
};
DarkMode.propTypes = {
  off: PropTypes.bool,
  children: PropTypes.any.isRequired // eslint-disable-line react/forbid-prop-types

};
DarkMode.defaultProps = {
  off: false
};
var useDarkMode = function useDarkMode() {
  return useContext(DarkContext);
};
var darkModeAware = function darkModeAware(Component) {
  return function (props) {
    return React.createElement(Component, _extends({
      dark: useDarkMode()
    }, props));
  };
};

/*
* OverridableThemeProvider allows using multiple copies of glu theming.
*
* This is useful in a microfrontend environment where you have a shared copy of theming and a local
* copy used to theme an individual microfrontend app. In this case you don't want theming from the
* microfrontend app to pollute the shared @glu/theming themeDefaults registry which is is a
* singleton with the defaults for all loaded components. As a singleton it would be polluted if
* @glu/buttons-react version 1.5.0 loaded as a shared lib and then a microfrontend app overrode that
* lib with version 2.0.1. Whichever version of buttons loads last will register its defaults with
* the shared theming. If there are differences then one copy of buttons will get the wrong defaults
* and may not function properly.
*
* This component avoids the issue by letting you safely override theming but still have all the
* theme defaults merge together properly. It does this by getting the theme and the baseTheme for
* your microfrontend app and setting up an outer theming provider with this apps theming. The result
* is then fed to the inner shared theming provider as the "theme" overrides. Both providers wrap
* your app so if a shared glu component is rendered it will get theming from the shared provider
* and if an overridden glu component is rendered it will get it from the microfrontend app specific
* theming provider.
*/

var OverridableThemeProvider = function OverridableThemeProvider(_ref) {
  var children = _ref.children,
      theme = _ref.theme,
      AppThemeProvider = _ref.AppThemeProvider,
      AppThemeConsumer = _ref.AppThemeConsumer,
      appBaseTheme = _ref.appBaseTheme,
      SharedThemeProvider = _ref.SharedThemeProvider,
      sharedBaseTheme = _ref.sharedBaseTheme;
  return React.createElement(AppThemeProvider, {
    baseTheme: appBaseTheme,
    theme: theme
  }, AppThemeProvider !== SharedThemeProvider ? React.createElement(AppThemeConsumer, null, function (appTheme) {
    return React.createElement(SharedThemeProvider, {
      baseTheme: sharedBaseTheme,
      theme: appTheme
    }, children);
  }) : React.createElement(React.Fragment, null, children));
};

OverridableThemeProvider.propTypes = {
  /** Your app's components under the ThemingProvider(s) */
  children: PropTypes.node.isRequired,

  /** Theme customizations for your microfrontend app */
  theme: PropTypes.shape({}),

  /**
   * ThemeProvider used for your app, may be the same as the shared ThemeProvider if you have
   * no overrides
   */
  AppThemeProvider: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.element]).isRequired,

  /**
   * ThemeConsumer used for your app, may be the same as the shared ThemeConsumer if you have
   * no overrides
   */
  AppThemeConsumer: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.element]).isRequired,

  /**
   * Base theme used for your app, ie ashTheme or legacyTheme, etc
   * May be the same as the sharedBaseTheme if you have no overrides
   */
  appBaseTheme: PropTypes.func.isRequired,

  /**
   * ThemeProvider used for your app from the shared theming, may be the same as the shared
   * AppThemeProvider if you have no overrides
   */
  SharedThemeProvider: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.element]).isRequired,

  /**
   * Base theme used for your app from the shared theming, ie ashTheme or legacyTheme, etc.
   * May be the same as the appBaseTheme if you have no overrides
   */
  sharedBaseTheme: PropTypes.func.isRequired
};
OverridableThemeProvider.defaultProps = {
  theme: {}
};

export { DarkContext, DarkMode, OverridableThemeProvider, ThemeConsumer, ThemeProvider, createUseStyles, darkModeAware, mergeClasses, theming, useDarkMode, useTheme, withStyles, withTheme };
